import { resolveAssetGrade, resolveAssetStatus } from "./assetStatus";
import type { HuelleAsset } from "./planModel";

export type HuelleAssetStaticColumnKey =
  | "external_id"
  | "name"
  | "location"
  | "plan"
  | "last_maintenance"
  | "result_status"
  | "status";

export type HuelleAssetColumnKey = HuelleAssetStaticColumnKey | `md_${string}`;

export const HUELLE_REQUIRED_COLUMNS = ["external_id"] as const;

export const HUELLE_STATIC_COLUMNS: HuelleAssetStaticColumnKey[] = [
  "external_id",
  "name",
  "location",
  "plan",
  "last_maintenance",
  "result_status",
  "status",
];

/** Standardmäßig sichtbar. */
export const HUELLE_DEFAULT_COLUMNS: HuelleAssetStaticColumnKey[] = [
  "external_id",
  "name",
  "result_status",
  "location",
  "plan",
  "last_maintenance",
];

export const HUELLE_HIDDEN_BY_DEFAULT: HuelleAssetStaticColumnKey[] = ["status"];

export const HUELLE_ASSET_DATA_COLUMNS = HUELLE_DEFAULT_COLUMNS;

export const HUELLE_COL_MIN_WIDTH = 72;
export const HUELLE_COL_MAX_WIDTH = 520;

const DEFAULT_WIDTHS: Record<HuelleAssetStaticColumnKey, number> = {
  external_id: 140,
  name: 220,
  location: 180,
  plan: 120,
  last_maintenance: 160,
  result_status: 240,
  status: 120,
};

const STATIC_LABELS: Record<HuelleAssetStaticColumnKey, string> = {
  external_id: "Kennung",
  name: "Bezeichnung",
  location: "Ort",
  plan: "Im Plan",
  last_maintenance: "Letzte Wartung",
  result_status: "Status",
  status: "Objektstatus",
};

const MASTERDATA_SKIP_KEYS = new Set([
  "hero_address_id",
  "sub_location_id",
  "external_id",
  "name",
  "bezeichnung",
  "plan_id",
  "plan_page",
  "plan_point",
  "label_text",
  "label_x",
  "label_y",
  "coordinate_mode",
  "page_number",
]);

export function isMasterdataColumn(key: string): key is `md_${string}` {
  return key.startsWith("md_");
}

export function masterdataColumnKey(fieldKey: string): `md_${string}` {
  return `md_${fieldKey}`;
}

export function masterdataFieldKeyFromColumn(col: string): string | null {
  if (!isMasterdataColumn(col)) return null;
  return col.slice(3);
}

export type HuelleColumnLabelMap = Record<string, string>;

export function getHuelleColumnLabel(
  key: string,
  labels: HuelleColumnLabelMap = {},
): string {
  if (key in STATIC_LABELS) {
    return STATIC_LABELS[key as HuelleAssetStaticColumnKey];
  }
  const fromMap = String(labels[key] || "").trim();
  if (fromMap) return fromMap;
  const fieldKey = masterdataFieldKeyFromColumn(key);
  if (fieldKey) {
    const fromFieldMap = String(labels[masterdataColumnKey(fieldKey)] || labels[fieldKey] || "").trim();
    if (fromFieldMap) return fromFieldMap;
    return fieldKey;
  }
  return key;
}

export function getHuelleColumnDefaultWidth(key: string): number {
  if (key in DEFAULT_WIDTHS) {
    return DEFAULT_WIDTHS[key as HuelleAssetStaticColumnKey];
  }
  return 140;
}

export function clampHuelleColumnWidth(width: number): number {
  return Math.min(HUELLE_COL_MAX_WIDTH, Math.max(HUELLE_COL_MIN_WIDTH, Math.round(width)));
}

export interface MasterdataColumnDef {
  columnKey: `md_${string}`;
  fieldKey: string;
  label: string;
}

/**
 * Stammdaten-Spalten aus dem Formularschema (mit Anzeigetiteln).
 * Fallback: Keys aus geladenen Objekten, falls noch kein Schema da ist.
 */
export function buildMasterdataColumnDefs(
  schemaFields: Array<{ field_key?: string; effective_label?: string; label?: string }>,
  assets: HuelleAsset[] = [],
): MasterdataColumnDef[] {
  const byField = new Map<string, MasterdataColumnDef>();

  for (const field of schemaFields) {
    const fieldKey = String(field.field_key || "").trim();
    if (!fieldKey || MASTERDATA_SKIP_KEYS.has(fieldKey)) continue;
    const label = String(field.effective_label || field.label || fieldKey).trim() || fieldKey;
    byField.set(fieldKey, {
      columnKey: masterdataColumnKey(fieldKey),
      fieldKey,
      label,
    });
  }

  if (!byField.size) {
    for (const asset of assets) {
      const md = asset.masterdata;
      if (!md || typeof md !== "object") continue;
      for (const [key, value] of Object.entries(md as Record<string, unknown>)) {
        const trimmed = String(key || "").trim();
        if (!trimmed || MASTERDATA_SKIP_KEYS.has(trimmed)) continue;
        if (value == null || value === "" || typeof value === "object") continue;
        if (byField.has(trimmed)) continue;
        byField.set(trimmed, {
          columnKey: masterdataColumnKey(trimmed),
          fieldKey: trimmed,
          label: trimmed,
        });
      }
    }
  }

  return [...byField.values()].sort((a, b) =>
    a.label.localeCompare(b.label, "de", { sensitivity: "base" }),
  );
}

export function masterdataColumnLabelMap(defs: MasterdataColumnDef[]): HuelleColumnLabelMap {
  const out: HuelleColumnLabelMap = {};
  for (const def of defs) {
    out[def.columnKey] = def.label;
    out[def.fieldKey] = def.label;
  }
  return out;
}

/** @deprecated Nutze buildMasterdataColumnDefs – bleibt als schlanker Helper. */
export function discoverMasterdataColumns(
  assets: HuelleAsset[],
  schemaFields: Array<{ field_key?: string; effective_label?: string; label?: string }> = [],
): `md_${string}`[] {
  return buildMasterdataColumnDefs(schemaFields, assets).map((def) => def.columnKey);
}

export function getAvailableHuelleColumns(masterdataCols: string[] = []): string[] {
  return [...HUELLE_STATIC_COLUMNS, ...masterdataCols.filter(isMasterdataColumn)];
}

export function getDefaultHuelleColumns(masterdataCols: string[] = []): string[] {
  void masterdataCols;
  return [...HUELLE_DEFAULT_COLUMNS];
}

export function normalizeHuelleColumnSelection(
  selection: string[] | undefined | null,
  available: string[],
): string[] {
  const out: string[] = [];
  if (Array.isArray(selection)) {
    for (const col of selection) {
      if (typeof col !== "string") continue;
      const trimmed = col.trim();
      if (!trimmed || !available.includes(trimmed)) continue;
      if (!out.includes(trimmed)) out.push(trimmed);
    }
  }
  for (const required of HUELLE_REQUIRED_COLUMNS) {
    if (available.includes(required) && !out.includes(required)) out.unshift(required);
  }
  if (!out.length) return getDefaultHuelleColumns().filter((c) => available.includes(c));
  return out;
}

function formatDate(value?: string | null): string {
  if (!value) return "Noch keine Wartung";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return new Intl.DateTimeFormat("de-DE", { dateStyle: "medium" }).format(d);
}


function formatMasterdataValue(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "boolean") return value ? "Ja" : "Nein";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value.trim();
  return "";
}

export function renderHuelleAssetCell(
  asset: HuelleAsset,
  col: string,
  locationFallback: string,
): string {
  if (isMasterdataColumn(col)) {
    const fieldKey = masterdataFieldKeyFromColumn(col);
    if (!fieldKey) return "";
    const md = asset.masterdata;
    if (!md || typeof md !== "object") return "";
    return formatMasterdataValue((md as Record<string, unknown>)[fieldKey]);
  }

  switch (col as HuelleAssetStaticColumnKey) {
    case "external_id":
      return String(asset.external_id || "").trim();
    case "name":
      return String(asset.name || "").trim();
    case "location":
      return String(asset.location_label || locationFallback || "Basisort").trim();
    case "plan":
      return asset.is_plan_positioned ? "Platziert" : "";
    case "last_maintenance":
      return formatDate(asset.last_performed_at);
    case "result_status": {
      const grade = resolveAssetGrade(asset);
      const label = resolveAssetStatus(asset).label;
      return grade != null ? `${label} ${grade}` : label;
    }
    case "status":
      return String(asset.status || "").trim();
    default:
      return "";
  }
}
